import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDebounce } from '../../../../hooks/useDebounce';
import { Box, Typography, CircularProgress, Stack, Alert } from '@mui/material';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import { useBooking } from '../../../../context/bookingContext';
import dayjs from 'dayjs';
import QuantityControl from './QuantityControl';
import { toast } from 'react-toastify';

function PersonSelect({
  selectedDate,
  selectedDuration,
  onPersonSelect,
  setCategoryPrices: setCategoryPricesProp,
}) {
  const {
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    selectedCategories,
    setSelectedCategories,
    setMaxAvailability,
    maxCapacity,
    updateCapacity,
    minPersons,
    previousSelectedDate,
    setPreviousSelectedDate,
    overbooking,
    setCategoryPrices,
    shouldWaitForResourceSelection,
    products,
    carryOverQuantities,
    tempReservationId,
  } = useBooking();

  // The reservation the modal is currently holding travels with the availability
  // request so the modal's own selection does not count against the capacity it
  // is asking about. Read through a ref: it must not retrigger a fetch, only
  // travel with the ones the selection triggers.
  const tempReservationIdRef = useRef(tempReservationId);
  tempReservationIdRef.current = tempReservationId;
  const [categories, setCategories] = useState([]);
  const [quantities, setQuantities] = useState(() => {
    const initialQuantities = {};
    selectedCategories.forEach(category => {
      initialQuantities[category.name] = category.quantity;
    });
    return initialQuantities;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedCategories, setHasFetchedCategories] = useState(false);
  const lastFetchState = useRef({ selectedResource: null });
  const prevOverbooking = useRef(overbooking);

  const { getCategories } = useInternalBooking();
  const shouldAutoApplyMinimum = categoryList => categoryList.length === 1;

  const fetchCategories = useCallback(async () => {
    if (!selectedDate || !selectedEntertainment || !selectedProduct) {
      setCategories([]);
      setQuantities({});
      setSelectedCategories([]);
      setHasFetchedCategories(false);
      return;
    }

    // If product has resources and none is selected yet, don't make the call
    if (shouldWaitForResourceSelection(selectedProduct)) {
      return;
    }

    // For products without resources, we need maxCapacity and minPersons to proceed
    if (
      !shouldWaitForResourceSelection(selectedProduct) &&
      (!maxCapacity || (!minPersons && minPersons !== 0))
    ) {
      return;
    }

    if (!selectedDate || !dayjs.isDayjs(selectedDate) || !selectedDate.isValid()) {
      setCategories([]);
      setQuantities({});
      setSelectedCategories([]);
      setHasFetchedCategories(false);
      return;
    }

    const currentDate = selectedDate.format('YYYY-MM-DD');
    const shouldFetch =
      !lastFetchState.current.date ||
      lastFetchState.current.date !== currentDate ||
      selectedProduct !== lastFetchState.current.selectedProduct ||
      selectedResource !== lastFetchState.current.selectedResource;

    if (!shouldFetch) {
      return;
    }

    setIsLoading(true);
    try {
      const previousFetchState = lastFetchState.current;

      const data = await getCategories(
        selectedEntertainment,
        selectedProduct,
        selectedDate.year(),
        selectedDate.month(),
        selectedDate.date(),
        selectedResource,
        tempReservationIdRef.current
      );

      if (data && data.categories) {
        setCategories(data.categories);
        setHasFetchedCategories(true);
        if (setCategoryPricesProp) {
          setCategoryPricesProp(data.categories);
        } else if (setCategoryPrices) {
          setCategoryPrices(data.categories);
        }

        // Always check and adjust quantities when capacity changes (date or resource change)
        const newQuantities = {};
        let totalCurrentQuantity = 0;

        const currentMaxCapacity =
          data.maxCapacity !== undefined ? data.maxCapacity : maxCapacity;
        const currentMinPersons =
          data.minParticipants !== undefined
            ? Math.max(minPersons, data.minParticipants)
            : minPersons;
        const autoApplyMinimum = shouldAutoApplyMinimum(data.categories);

        if (data.maxCapacity !== undefined) {
          setMaxAvailability(data.maxCapacity);
          // Avoid overriding duration-based caps when a duration is selected
          if (!selectedDuration) {
            updateCapacity(null, data.maxCapacity, data.minParticipants);
          }
        }

        // Existing quantities only survive while the resource stays the same; a
        // total above the new capacity is dropped entirely.
        if (selectedResource !== previousFetchState.selectedResource) {
          data.categories.forEach(category => {
            newQuantities[category.name] = 0;
          });
        } else {
          data.categories.forEach(category => {
            const existingQuantity = quantities[category.name] || 0;
            newQuantities[category.name] = existingQuantity;
            totalCurrentQuantity += existingQuantity;
          });

          if (
            !overbooking &&
            currentMaxCapacity !== null &&
            totalCurrentQuantity > currentMaxCapacity
          ) {
            data.categories.forEach(category => {
              newQuantities[category.name] = 0;
            });
          }
        }

        // Cart flow: when nothing is selected yet, pre-fill the person counts of
        // the previous cart item (it is the same group of people). Matched by
        // category name, with the total carried into the first category when no
        // name matches. Runs before the minimum auto-fill so the carried counts
        // take precedence over the category minimum; a total above the capacity
        // is dropped. The counts stay freely adjustable afterwards.
        const sumQuantities = () =>
          data.categories.reduce((sum, c) => sum + (newQuantities[c.name] || 0), 0);
        if (sumQuantities() === 0 && carryOverQuantities) {
          let seededTotal = 0;
          data.categories.forEach(category => {
            const carried = carryOverQuantities[category.name] || 0;
            if (carried > 0) {
              newQuantities[category.name] = carried;
              seededTotal += carried;
            }
          });
          if (seededTotal === 0 && data.categories.length > 0) {
            const totalCarried = Object.values(carryOverQuantities).reduce(
              (sum, quantity) => sum + (quantity || 0),
              0
            );
            if (totalCarried > 0) {
              newQuantities[data.categories[0].name] = totalCarried;
              seededTotal = totalCarried;
            }
          }
          if (!overbooking && currentMaxCapacity !== null && seededTotal > currentMaxCapacity) {
            data.categories.forEach(category => {
              newQuantities[category.name] = 0;
            });
          }
        }

        // Only auto-apply the minimum when there is exactly one selectable category.
        if (autoApplyMinimum && currentMinPersons !== null) {
          const total = sumQuantities();
          if (total < currentMinPersons) {
            const categoriesWithQuantities = data.categories.filter(
              category => newQuantities[category.name] > 0
            );
            if (categoriesWithQuantities.length > 0) {
              newQuantities[categoriesWithQuantities[0].name] += currentMinPersons - total;
            } else if (data.categories.length > 0) {
              newQuantities[data.categories[0].name] = currentMinPersons;
            }
          }
        }

        setQuantities(newQuantities);

        const newSelectedCategories = data.categories
          .filter(category => newQuantities[category.name] > 0)
          .map(category => ({
            id: category.id,
            name: category.name,
            quantity: newQuantities[category.name],
          }));
        setSelectedCategories(newSelectedCategories);

        if (previousFetchState.date !== currentDate) {
          setPreviousSelectedDate(selectedDate);
        }

        lastFetchState.current = {
          date: currentDate,
          selectedResource,
          selectedProduct,
        };
      } else {
        setCategories([]);
        setQuantities({});
        setSelectedCategories([]);
        setHasFetchedCategories(true);
        if (previousFetchState.date !== currentDate) {
          setPreviousSelectedDate(selectedDate);
        }

        lastFetchState.current = {
          date: currentDate,
          selectedResource,
          selectedProduct,
        };
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Fehler beim Laden der Personenkategorien.');
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedDate,
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    maxCapacity,
    minPersons,
    overbooking,
    quantities,
    carryOverQuantities,
    getCategories,
    setCategoryPricesProp,
    setCategoryPrices,
    setSelectedCategories,
    setPreviousSelectedDate,
    shouldWaitForResourceSelection,
  ]);

  const debouncedFetchCategories = useDebounce(fetchCategories, 150);

  useEffect(() => {
    // Only run when overbooking is switched from true to false
    if (prevOverbooking.current && !overbooking) {
      const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0);

      if (categories.length > 0 && maxCapacity !== null && totalQuantity > maxCapacity) {
        const newQuantities = {};
        const autoApplyMinimum = shouldAutoApplyMinimum(categories);

        categories.forEach((category, index) => {
          if (index === 0 && autoApplyMinimum && minPersons > 0) {
            newQuantities[category.name] = minPersons;
          } else {
            newQuantities[category.name] = 0;
          }
        });

        setQuantities(newQuantities);
        const newSelectedCategories = categories
          .map(category => ({
            id: category.id,
            name: category.name,
            quantity: newQuantities[category.name] || 0,
          }))
          .filter(c => c.quantity > 0);
        setSelectedCategories(newSelectedCategories);
      }
    }

    prevOverbooking.current = overbooking;
  }, [overbooking, maxCapacity, minPersons, categories, quantities, setSelectedCategories]);

  useEffect(() => {
    if (!selectedDate || !dayjs.isDayjs(selectedDate) || !selectedDate.isValid()) {
      setCategories([]);
      setQuantities({});
      setSelectedCategories([]);
      setHasFetchedCategories(false);
      return;
    }
    debouncedFetchCategories();
  }, [
    selectedDate,
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    previousSelectedDate,
    maxCapacity,
    minPersons,
    overbooking,
    debouncedFetchCategories,
    shouldWaitForResourceSelection,
  ]);

  useEffect(() => {
    if (selectedCategories.length > 0) {
      const newQuantities = {};
      selectedCategories.forEach(category => {
        newQuantities[category.name] = category.quantity;
      });
      setQuantities(newQuantities);
    }
  }, [selectedCategories]);

  const handleQuantityChange = (categoryName, value) => {
    const newValue = parseInt(value) || 0;
    const currentMaxCapacity = maxCapacity;
    const currentMinPersons = minPersons;
    const autoApplyMinimum = shouldAutoApplyMinimum(categories);

    const currentTotal = Object.entries(quantities).reduce((total, [name, qty]) => {
      if (name === categoryName) return total + newValue;
      return total + qty;
    }, 0);

    // Allow adding quantities even if below minimum when starting from 0
    // This prevents the controls from becoming unusable after reset
    const isStartingFromZero = Object.values(quantities).every(qty => qty === 0);

    if (!overbooking && currentMaxCapacity !== null && currentTotal > currentMaxCapacity) {
      return;
    }

    // Only enforce the minimum directly when there is exactly one category
    if (
      autoApplyMinimum &&
      !isStartingFromZero &&
      currentMinPersons !== null &&
      currentTotal < currentMinPersons
    ) {
      return;
    }

    const newQuantities = { ...quantities };
    newQuantities[categoryName] = newValue;
    setQuantities(newQuantities);

    const newSelectedCategories = categories
      .filter(category => newQuantities[category.name] > 0)
      .map(category => ({
        id: category.id,
        name: category.name,
        quantity: newQuantities[category.name],
      }));
    setSelectedCategories(newSelectedCategories);
    onPersonSelect(newSelectedCategories);
  };

  const displayCategories = categories;

  const derivedGroupType = useMemo(() => {
    const productObj = products.find(p => p._id === selectedProduct);
    const mapping = productObj?.ageSplitMapping;
    if (!mapping) return null;
    const kidsNames = new Set(mapping.kids || []);
    const adultsNames = new Set(mapping.adults || []);
    let kidsCount = 0;
    let adultsCount = 0;
    for (const [name, qty] of Object.entries(quantities)) {
      if (kidsNames.has(name)) kidsCount += qty;
      else if (adultsNames.has(name)) adultsCount += qty;
    }
    if (kidsCount === 0 && adultsCount === 0) return null;
    if (kidsCount > adultsCount) return 'kids';
    if (adultsCount > kidsCount) return 'adults';
    return 'kids';
  }, [products, selectedProduct, quantities]);

  // do not render when date is not valid
  if (!selectedDate || !dayjs.isDayjs(selectedDate) || !selectedDate.isValid()) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction='column' spacing={2} alignItems='left'>
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant='body2' color='text.secondary'>
              Personenkategorien werden geladen...
            </Typography>
          </Box>
        )}
        {!isLoading && hasFetchedCategories && displayCategories.length === 0 && (
          <Alert severity='info'>Keine Personenkategorien gefunden.</Alert>
        )}
        {displayCategories.map(category => {
          const currentValue = quantities[category.name] || 0;
          const currentTotal = Object.entries(quantities).reduce((total, [name, qty]) => {
            if (name === category.name) return total + currentValue;
            return total + qty;
          }, 0);
          const maxForCategory = overbooking
            ? undefined
            : maxCapacity
              ? maxCapacity - (currentTotal - currentValue)
              : undefined;
          const autoApplyMinimum = shouldAutoApplyMinimum(categories);
          const minForCategory =
            autoApplyMinimum && minPersons !== null
              ? Math.max(0, minPersons - (currentTotal - currentValue))
              : 0;

          return (
            <Box
              key={`category-${category.name}`}
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
              }}
            >
              <Typography
                variant='body1'
                sx={{
                  minWidth: { sm: '140px' },
                  flex: { sm: '0 0 auto' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Anzahl {category.name}
              </Typography>
              <QuantityControl
                value={currentValue}
                onChange={value => handleQuantityChange(category.name, value)}
                min={minForCategory}
                max={maxForCategory}
                disabled={categories.length === 0}
              />
            </Box>
          );
        })}
        {derivedGroupType && (
          <Typography variant='body2' color='info.main' sx={{ fontStyle: 'italic' }}>
            {derivedGroupType === 'kids'
              ? 'Diese Buchung wird als Kindergruppe geführt'
              : 'Diese Buchung wird als Erwachsenengruppe geführt'}
          </Typography>
        )}
        <Box sx={{ gap: 0.5 }}>
          {categories.length > 0 &&
            maxCapacity !== null &&
            maxCapacity !== Infinity &&
            !overbooking &&
            !isLoading && (
              <Typography variant='body2' color='text.secondary'>
                Max. {maxCapacity} Teilnehmer
              </Typography>
            )}
          {categories.length > 0 && minPersons !== null && (
            <Typography variant='body2' color='text.secondary'>
              Min. {minPersons > 0 ? minPersons : 1} Teilnehmer
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default PersonSelect;
